import { Injectable } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { Roles, User } from '../interfaces/user';
import { AuthService } from './authentication/auth.service';

// Shape of the claims we care about, decoded from the ACCESS token (see AuthService.getAccessTokenClaims).
// Not necessarily the same as `account.idTokenClaims` — Azure AD can expose different claims per token type.
interface AppTokenClaims {
	roles?: string[];
	groups?: string[];
	preferred_username?: string;
	given_name?: string;
	family_name?: string;
	name?: string;
}

// Maps the raw Azure AD `roles` claim to the app's internal Roles enum. These App Role values
// are the EXACT same strings as the pre-existing Cognito group names
// (`PGEbe-<env>-be-product-catalog-admin` / `...-tariff-card-admin`, see this repo's backend
// `SecurityConstants`/`ENV_COGNITO_AUTHORITY_PRODUCT_CATALOG_ADMIN`) — matching on the trailing
// suffix (not the full value) keeps this stable across the per-environment infix
// (die/bas/bag/prd), without needing to inject the exact per-env string into this frontend.
//
// v1 (2026-09-08) assumed a `_ADMINISTRATOR`/`_AGENT` suffix borrowed from document-service's
// convention — product-catalog's real App Roles have no underscore and no "AGENT" tier at all;
// a user with neither role below simply falls back to Roles.USER (see setUserFromMsal).
function mapAzureRolesToAppRole(rawRoles: string[] | undefined): Roles | undefined {
	if (!rawRoles || rawRoles.length === 0) {
		return undefined;
	}

	// Most-privileged-first: a user assigned both roles is treated as a full ADMIN.
	if (rawRoles.some((role) => role.endsWith('-product-catalog-admin'))) {
		return Roles.ADMIN;
	}
	if (rawRoles.some((role) => role.endsWith('-product-catalog-tariff-card-admin'))) {
		return Roles.TARIFF_CARD_ADMIN;
	}

	return undefined;
}

@Injectable({
	providedIn: 'root'
})
export class UserService {
	role: Roles;
	private user$: BehaviorSubject<User> = new BehaviorSubject<User>(null);

	constructor(private readonly authService: AuthService) {}

	/**
	 * `account.idTokenClaims` reflects the ID token only. `roles`/`groups` are confirmed present
	 * on the ACCESS token (the one attached to real API calls) — they are NOT guaranteed to be
	 * present, or identical, on the ID token. So we fetch the access token for this app's own API
	 * scope and decode ITS payload for `roles`/`groups`, instead of trusting `idTokenClaims`.
	 */
	async setUserFromMsal(account: AccountInfo): Promise<void> {
		const idClaims = account.idTokenClaims as AppTokenClaims | undefined;
		const accessClaims = await this.authService.getAccessTokenClaims<AppTokenClaims>(environment.apiConfig.scopes);

		const rawRoles = accessClaims?.groups ?? idClaims?.groups;
		const mappedRole = mapAzureRolesToAppRole(rawRoles);

		if (!mappedRole) {
			// Do NOT silently default to ADMIN here — that was the previous bug (hardcoded
			// Roles.ADMIN regardless of the token content). USER is the safer fallback.
			console.error(
				'[UserService] No known role suffix found in the Azure AD "roles" claim:',
				rawRoles,
				'— falling back to Roles.USER. Update mapAzureRolesToAppRole if a new App Role was declared.'
			);
		}

		// Groups: only the raw Object IDs (GUIDs) from the `groups` claim are kept — NOT resolved
		// to display names. A Microsoft Graph resolution attempt (`directoryObjects/getByIds`) was
		// tried and removed (2026-09-08): it fails with AADSTS65001 (consent_required) on the
		// current borrowed `BeDocumentServiceIpFr` app registration, which has no Group.Read.All
		// consent granted. Re-introduce group name resolution only once IT grants that consent on
		// product-catalog's OWN app registration — see TODO-NEXT.md.
		const groupIds = accessClaims?.groups ?? idClaims?.groups ?? [];

		const user: User = {
			role: mappedRole ?? Roles.USER,
			id: account.localAccountId ?? account.homeAccountId,
			username: account.username,
			email: (idClaims?.preferred_username as string) ?? account.username,
			firstName: (idClaims?.given_name as string) ?? account.name,
			lastName: (idClaims?.family_name as string) ?? '',
			accountName: account.username,
			groupIds
		};

		this.user$.next(user);
	}

	getUser() {
		return this.user$.value;
	}

	hasAdminRole() {
		const r = this.getUser()?.role;

		return r === Roles.BUSINESS_ADMIN || r === Roles.ADMIN;
	}

	hasUserRole() {
		return this.getUser()?.role === Roles.USER;
	}

	hasTariffCardAdminRole() {
		return this.getUser()?.role === Roles.TARIFF_CARD_ADMIN;
	}
}
